import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Excel, GenericDoc, Pdf } from '@fluentsms/agentnet-web-icons';
import { CircularProgress, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircleOutlined, ClearOutlined, DeleteOutlined, ErrorOutlineOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { transitionSpeed } from '../../theme';
export const UploadStatus = ({ className, errors, file, fullWidth, isLoading, hideDeleteButton, hideClearButton = false, isUploaded, progress, truncateFileName = true, onClick, onRemove, }) => {
    const useStylesUploadStatus = makeStyles((theme) => ({
        file: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1, 2),
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: fullWidth ? '100%' : 250,
        },
        fileError: {},
        fileIcon: {
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
        },
        fileInfo: {
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(1),
            height: 39,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexDirection: 'column',
            flex: 1,
        },
        fileDelete: {
            marginLeft: 'auto',
            padding: theme.spacing(0.5),
            '& svg': {
                width: 20,
                height: 20,
            },
        },
        fileName: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            userSelect: 'none',
        },
        fileMeta: {
            '&>p': {
                whiteSpace: 'nowrap',
            },
        },
        toolTip: {
            '&:hover': {
                cursor: 'default',
            },
        },
        isUploaded: {
            transition: `all ${transitionSpeed} ease`,
            cursor: 'pointer',
            '& .fileInfo': {
                height: 34,
                width: '100%',
                pointerEvents: 'none',
            },
            '&:hover': {
                boxShadow: `0 2px ${theme.palette.action.focus}`,
            },
        },
    }));
    const hasError = errors && errors.length > 0;
    const classes = useStylesUploadStatus();
    const formatFileName = (name, maxLength = 20) => {
        if (name.length <= maxLength)
            return name;
        const extension = name.split('.').pop() || '';
        const nameWithoutExtension = name.substring(0, name.lastIndexOf('.'));
        const truncatedName = nameWithoutExtension.substring(0, maxLength - 3 - extension.length);
        return `${truncatedName}...${extension}`;
    };
    const formatFileSize = (bytes) => {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    const getFileType = (fileName) => {
        if (!fileName)
            return '';
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return 'pdf';
            case 'xls':
            case 'xlsx':
                return 'excel';
            default:
                return 'file';
        }
    };
    const getFileIconByType = (fileType) => {
        if (fileType === 'pdf') {
            return _jsx(Pdf, { width: '18', height: '18', fill: '#B71C1C' });
        }
        if (fileType === 'excel') {
            return _jsx(Excel, { width: '18', height: '18', fill: '#33691E' });
        }
        return _jsx(GenericDoc, { width: '18', height: '18', fill: '#65788F' });
    };
    const renderIcon = () => {
        if (hasError) {
            return _jsx(ErrorOutlineOutlined, { color: 'error' });
        }
        if (isUploaded) {
            return getFileIconByType(getFileType(file.name));
        }
        if (progress === 100) {
            return _jsx(CheckCircleOutlined, { htmlColor: '#43A047', "aria-label": 'File Accepted', fontSize: 'small' });
        }
        if (isLoading) {
            return _jsx(CircularProgress, { variant: 'indeterminate', size: 22, "aria-label": 'Uploading file' });
        }
        return _jsx(CircularProgress, { variant: 'determinate', value: progress || 65, "aria-label": 'Upload progress', size: 22 });
    };
    return (_jsxs("div", { className: clsx(classes.file, className && className, {
            [classes.fileError]: hasError,
            [classes.isUploaded]: isUploaded,
        }), onClick: () => onClick && onClick(file.name), children: [_jsx("div", { className: clsx(classes.fileIcon), children: renderIcon() }), _jsxs("div", { className: clsx(classes.fileInfo, 'fileInfo'), children: [_jsx(Tooltip, { title: file.name, className: classes.toolTip, arrow: true, placement: 'top', children: _jsx(Typography, { variant: 'body1', className: classes.fileName, children: truncateFileName ? formatFileName(file.name) : file.name }) }), !isUploaded && (_jsx("div", { className: classes.fileMeta, children: hasError ? (errors.map((error, i) => (_jsx(Typography, { variant: 'body2', color: 'error', children: error }, i)))) : (_jsx(Typography, { variant: 'caption', color: 'textSecondary', children: formatFileSize(file.size) })) }))] }), !isUploaded && !hideDeleteButton && (_jsx(IconButton, { className: classes.fileDelete, disableFocusRipple: true, disableRipple: true, focusRipple: false, disableTouchRipple: true, disabled: false, onClick: () => onRemove && onRemove(file.name), children: (isLoading || (progress && progress < 100)) && !hideClearButton ? (_jsx(ClearOutlined, { color: 'secondary', fontSize: 'small', "aria-label": `Cancel Upload` })) : progress === 100 ? (_jsx(DeleteOutlined, { color: 'secondary', fontSize: 'small', "aria-label": `Delete ${file.name}` })) : null }))] }));
};
